import { DEFAULT_EDITOR_TOOL, PanelType } from '@editor/config/constants'
import { PointArray } from '@editor/interfaces/attribute'
import { TransformTypes } from '@editor/interfaces/canvas'
import { EditorPermissionSet } from '@editor/interfaces/validation'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

export interface EditorState {
  /**
   * Whether the initial camera setup has been completed.
   */
  initCameraCompleted: boolean

  /**
   * Whether the initial controls setup has been completed.
   */
  initControlsCompleted: boolean

  /**
   * User is allowed to do modification for editor-related actions
   */
  permissionSet: EditorPermissionSet

  /**
   * Currently selected tool in the editor.
   */
  selectedTool: string

  /**
   * Previously selected tool in the editor.
   */
  prevSelectedTool: string

  /**
   * The tool selected before the previous one.
   */
  prevPrevSelectedTool: string

  /**
   * Currently selected sub-tool in the editor.
   */
  selectedSubTool?: string

  /**
   * Flag to indicate if volume value should be hidden.
   */
  hidePlaneLabel: boolean

  /**
   * Shape IDs of objects that anchor can be placed on.
   * Once this is set, every other settings will be ignored.
   * Even PCD will be ignored.
   */
  anchorPlacementObjects: string[]

  /**
   * Allowed transform controls.
   */
  allowedTransformControls?: TransformTypes[]

  /**
   * Current transform type.
   */
  currentTransformType?: TransformTypes

  /**
   * Flag to indicate if transform controls are disabled.
   */
  disableTransformControls: boolean

  /**
   * Shape IDs of objects that needs to be dimmed.
   */
  dimmedElementIds: string[]

  /**
   * Flag to indicate if a job is currently running.
   */
  isJobRunning: boolean

  /**
   * Whether there's any saving for items in elements panel.
   */
  isBatchSaving: boolean

  /**
   * The tool where the job is currently running.
   */
  jobRunningTool: string

  /**
   * Flag to indicate if panning is disabled.
   */
  disablePanning: boolean

  /**
   * Flag to indicate if rotation is disabled.
   */
  disableRotation: boolean

  /**
   * Flag to indicate if the point cloud is visible.
   */
  isPcdVisible: boolean

  /**
   * Bounding box of the PCD.
   * Consists of 2 points, min and max.
   */
  pcdBoundingBox?: [PointArray, PointArray]

  /**
   * Radius of the PCD bounding sphere.
   */
  pcdBoundingSphereRadius: number

  /**
   * Center point of the PCD bounding box.
   */
  pcdBoundingBoxCenter?: PointArray

  /**
   * Flag to indicate if element is being deleted.
   */
  isElementDeleting: boolean

  /**
   * List of collapsed panels.
   */
  collapsedPanel: PanelType[]

  /**
   * Flag to indicate if the file processing loading screen should be forced.
   */
  forceFileProcessingLoading?: boolean
}

const initialState: EditorState = {
  initCameraCompleted: false,
  initControlsCompleted: false,

  selectedTool: DEFAULT_EDITOR_TOOL,
  prevSelectedTool: DEFAULT_EDITOR_TOOL,
  prevPrevSelectedTool: DEFAULT_EDITOR_TOOL,

  permissionSet: {
    BROWSE: [],
    MODIFY: [],
    MODIFY_DISPLAY_SETTINGS: [],
  },
  hidePlaneLabel: false,
  anchorPlacementObjects: [],
  dimmedElementIds: [],
  collapsedPanel: [PanelType.Settings],
  isJobRunning: false,
  isBatchSaving: false,
  jobRunningTool: '',
  disablePanning: false,
  disableRotation: false,
  isElementDeleting: false,

  isPcdVisible: true,
  pcdBoundingSphereRadius: 0,

  disableTransformControls: false,
}

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    /**
     * Tool and sub-tool selections
     */
    setSelectedTool: (state, action: PayloadAction<string>) => {
      state.prevPrevSelectedTool = state.prevSelectedTool
      state.prevSelectedTool = state.selectedTool
      state.selectedTool = action.payload
    },
    setPrevSelectedTool: (state, action: PayloadAction<string>) => {
      state.prevPrevSelectedTool = state.prevSelectedTool
      state.prevSelectedTool = action.payload
    },
    setPrevPrevSelectedTool: (state, action: PayloadAction<string>) => {
      state.prevPrevSelectedTool = action.payload
    },
    setSelectedSubTool: (state, action: PayloadAction<string | undefined>) => {
      state.selectedSubTool = action.payload
    },

    /**
     * Editor-global states
     */
    setPermissionSet: (state, action: PayloadAction<EditorPermissionSet>) => {
      state.permissionSet = action.payload
    },
    setJobRunning: (state, { payload: { running, tool } }: PayloadAction<{ running: boolean; tool: string }>) => {
      state.isJobRunning = running
      state.jobRunningTool = running ? tool : ''
    },
    setIsBatchSaving: (state, action: PayloadAction<boolean>) => {
      state.isBatchSaving = action.payload
    },
    setInitCameraCompleted: (state, action: PayloadAction<boolean>) => {
      state.initCameraCompleted = action.payload
    },
    setInitControlsCompleted: (state, action: PayloadAction<boolean>) => {
      state.initControlsCompleted = action.payload
    },

    /**
     * PCD controls
     */
    setDisablePanning: (state, action: PayloadAction<boolean>) => {
      state.disablePanning = action.payload
    },
    setDisableRotation: (state, action: PayloadAction<boolean>) => {
      state.disableRotation = action.payload
    },

    /**
     * Canvas flags
     */
    setHidePlaneLabel: (state, action: PayloadAction<boolean>) => {
      state.hidePlaneLabel = action.payload
    },
    setAnchorPlacementObjects: (state, action: PayloadAction<string[]>) => {
      state.anchorPlacementObjects = action.payload
    },
    setForceFileProcessingLoading: (state, action: PayloadAction<boolean | undefined>) => {
      state.forceFileProcessingLoading = action.payload
    },

    /**
     * Set PCD/Mesh details
     */
    setPcdDetails: (
      state,
      {
        payload: { pcdBoundingBox, pcdBoundingSphereRadius, pcdBoundingBoxCenter },
      }: PayloadAction<{
        pcdBoundingBox: [PointArray, PointArray]
        pcdBoundingSphereRadius: number
        pcdBoundingBoxCenter: PointArray
      }>,
    ) => {
      state.pcdBoundingBox = pcdBoundingBox
      state.pcdBoundingSphereRadius = pcdBoundingSphereRadius
      state.pcdBoundingBoxCenter = pcdBoundingBoxCenter
    },
    setIsPcdVisible: (state, action: PayloadAction<boolean>) => {
      state.isPcdVisible = action.payload
    },

    /**
     * Element-related actions
     */
    addDimmedElementId: (state, action: PayloadAction<string>) => {
      state.dimmedElementIds.push(action.payload)
    },
    removeDimmedElementId: (state, action: PayloadAction<string>) => {
      state.dimmedElementIds = state.dimmedElementIds.filter((id) => id !== action.payload)
    },
    setElementDeleting: (state, action: PayloadAction<boolean>) => {
      state.isElementDeleting = action.payload
    },

    /**
     * Controls
     */
    setAllowedTransformControls: (state, action: PayloadAction<TransformTypes[]>) => {
      state.allowedTransformControls = action.payload
      state.currentTransformType = action.payload[0] // eslint-disable-line
    },
    setCurrentTransformType: (state, action: PayloadAction<TransformTypes>) => {
      state.currentTransformType = action.payload
    },
    setDisableTransformControls: (state, action: PayloadAction<boolean>) => {
      state.disableTransformControls = action.payload
    },
    resetTransformControls: (state) => {
      state.allowedTransformControls = undefined
      state.currentTransformType = undefined
      state.disableTransformControls = false
    },

    /**
     * Panel collapse state
     */
    setCollapsedPanel: (state, { payload }: PayloadAction<PanelType[]>) => {
      state.collapsedPanel = payload
    },
    openPanel: (state, { payload }: PayloadAction<PanelType>) => {
      if (state.collapsedPanel.includes(payload)) {
        const index = state.collapsedPanel.indexOf(payload)
        state.collapsedPanel.splice(index, 1)
      }
    },
    toggleCollapsedPanel: (state, { payload }: PayloadAction<PanelType>) => {
      const index = state.collapsedPanel.indexOf(payload)
      if (index === -1) {
        state.collapsedPanel.push(payload)
      } else {
        state.collapsedPanel.splice(index, 1)
      }
    },
    forceOpenToolPanel: (state) => {
      if (state.collapsedPanel.includes(PanelType.Tool)) {
        state.collapsedPanel = state.collapsedPanel.filter((type) => type !== PanelType.Tool)
      }
    },

    /**
     * Resets
     */
    reset: () => initialState,
  },
})

export const {
  // Tool and sub-tool selections
  setSelectedTool,
  setPrevSelectedTool,
  setPrevPrevSelectedTool,
  setSelectedSubTool,

  // Editor-global states
  setPermissionSet,
  setJobRunning,
  setIsBatchSaving,
  setInitCameraCompleted,
  setInitControlsCompleted,

  // PCD controls
  setDisablePanning,
  setDisableRotation,

  // Canvas flags
  setHidePlaneLabel,
  setAnchorPlacementObjects,
  setForceFileProcessingLoading,

  // PCD
  setPcdDetails,
  setIsPcdVisible,

  // Element-related actions
  addDimmedElementId,
  removeDimmedElementId,
  setElementDeleting,

  // Controls
  setAllowedTransformControls,
  setCurrentTransformType,
  setDisableTransformControls,
  resetTransformControls,

  // Panel collapse state
  setCollapsedPanel,
  openPanel,
  toggleCollapsedPanel,
  forceOpenToolPanel,

  // Resets
  reset,
} = editorSlice.actions

export default editorSlice.reducer
